import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import { useAuthStore } from './stores/auth';
import { usePortalAuthStore } from './stores/portalAuth';
import { useFeatureAccess, type PageKey, ALL_PAGES } from './composables/useFeatureAccess';
import { setAppTitle } from './utils/environment';
import { installLegacyModalDrag } from './utils/modalDrag';

// Bootstrap CSS and JS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// AG Grid CSS
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

// Custom styles
import './assets/styles.css';
import './styles/drag-drop.css';

// Router configuration — SPA mounted at root '/'. The old '/app/' prefix
// is preserved as a server-side 301 redirect during the migration window.
const router = createRouter({
  history: createWebHistory('/'),
  // Back/forward returns to where you were; query-only changes (persisted
  // filter sync via useViewState) never jump the page.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.path === from.path) return undefined;
    return { top: 0 };
  },
  routes: [
    // Old '/app/' links: prod/staging 301 these via nginx, but local dev has no
    // nginx — so mirror it client-side so '/app/...' doesn't dead-end to a blank.
    {
      path: '/app/:rest(.*)',
      redirect: (to) => ({ path: `/${to.params.rest}`, query: to.query, hash: to.hash }),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/LoginView.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Social sign-in redirect target — exchanges the handoff code for tokens.
      path: '/login/oauth/callback',
      name: 'oauth-callback',
      component: () => import('./views/OAuthCallbackView.vue'),
      meta: { requiresAuth: false },
    },
    {
      // MCP connector consent page (OAuth authorize hand-off). Requires a
      // signed-in user; the guard preserves the query string through login.
      path: '/mcp-authorize',
      name: 'mcp-authorize',
      component: () => import('./views/McpAuthorizeView.vue'),
    },
    {
      path: '/share/:tenant/t/:token',
      name: 'share-portal',
      component: () => import('./views/SharePortalView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/accept-invitation',
      name: 'accept-invitation',
      component: () => import('./views/AcceptInvitationView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('./views/VerifyEmailView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('./views/SignupView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('./views/ForgotPasswordView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('./views/ResetPasswordView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('./views/CheckoutView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/checkout/success',
      name: 'checkout-success',
      component: () => import('./views/CheckoutSuccessView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/download',
      name: 'download',
      component: () => import('./views/DownloadView.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Public API developer reference (RapiDoc over /api/v1/openapi.json).
      path: '/developers',
      name: 'developers',
      component: () => import('./views/DeveloperPortalView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/quote-view/:tenantSlug/:hash',
      name: 'quote-view',
      component: () => import('./views/QuotePublicView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/rfq-response/:tenantSlug/:token',
      name: 'rfq-response',
      component: () => import('./views/RFQResponseView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/rfi-response/:tenantSlug/:token',
      name: 'rfi-response',
      component: () => import('./views/RFIResponseView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/supplier-onboarding/:tenantSlug/:token',
      name: 'supplier-onboarding',
      component: () => import('./views/SupplierOnboardingView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/shared/report/:token',
      name: 'shared-report',
      component: () => import('./views/SharedReportView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/timesheet/me/:tenantSlug',
      name: 'timesheet-me',
      component: () => import('./views/TimesheetMeView.vue'),
      meta: { requiresAuth: false },
    },
    // ── Client portal (homeowner) — separate portal auth, mobile-first ──────────
    {
      path: '/portal/:tenantSlug/login',
      name: 'portal-login',
      component: () => import('./views/portal/PortalLoginView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug/accept-invite',
      name: 'portal-accept-invite',
      component: () => import('./views/portal/PortalAcceptInviteView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug/forgot-password',
      name: 'portal-forgot-password',
      component: () => import('./views/portal/PortalForgotPasswordView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug/reset-password',
      name: 'portal-reset-password',
      component: () => import('./views/portal/PortalResetPasswordView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      // Staff "View as client" hand-off: the preview token arrives in the URL
      // FRAGMENT (never sent to the server or access logs) and is moved into
      // sessionStorage before entering the portal.
      path: '/portal/:tenantSlug/preview',
      name: 'portal-preview',
      component: () => import('./views/portal/PortalPreviewView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug',
      name: 'portal-dashboard',
      component: () => import('./views/portal/PortalDashboardView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug/jobs/:jobNo',
      name: 'portal-job',
      component: () => import('./views/portal/PortalJobView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/portal/:tenantSlug/account',
      name: 'portal-account',
      component: () => import('./views/portal/PortalAccountView.vue'),
      meta: { requiresAuth: false, portal: true },
    },
    {
      path: '/download/ready',
      name: 'download-ready',
      component: () => import('./views/DownloadReadyView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('./views/OnboardingView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('./views/AdminView.vue'),
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
        skipTenantContext: true,
      },
    },
    {
      path: '/',
      component: () => import('./layouts/MainLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/dashboard',
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('./views/DashboardView.vue'),
        },
        {
          path: 'jobs',
          name: 'jobs',
          component: () => import('./views/JobsView.vue'),
        },
        {
          path: 'boq',
          name: 'boq',
          component: () => import('./views/BOQView.vue'),
        },
        {
          path: 'catalogue',
          name: 'catalogue',
          component: () => import('./views/CatalogueView.vue'),
        },
        {
          path: 'sales',
          name: 'sales',
          component: () => import('./views/SalesView.vue'),
        },
        {
          path: 'quotes',
          name: 'quotes',
          component: () => import('./views/QuotesView.vue'),
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('./views/OrdersView.vue'),
        },
        {
          path: 'rfqs',
          name: 'rfqs',
          component: () => import('./views/RFQView.vue'),
          meta: { title: 'RFQs' },
        },
        {
          path: 'rfis',
          name: 'rfis',
          component: () => import('./views/RFIsView.vue'),
          meta: { title: 'RFIs' },
        },
        {
          path: 'inspections',
          name: 'inspections',
          component: () => import('./views/InspectionsView.vue'),
          meta: { title: 'Inspections' },
        },
        {
          path: 'inspections/:id/run',
          name: 'inspection-run',
          component: () => import('./views/InspectionRunView.vue'),
          meta: { title: 'Conduct Inspection' },
        },
        {
          path: 'defects',
          name: 'defects',
          component: () => import('./views/DefectsView.vue'),
          meta: { title: 'Defects' },
        },
        {
          path: 'safety',
          name: 'safety',
          component: () => import('./views/SafetyView.vue'),
          meta: { title: 'Site Safety' },
        },
        {
          path: 'daily-logs',
          name: 'daily-logs',
          component: () => import('./views/DailyLogsView.vue'),
          meta: { title: 'Daily Logs' },
        },
        {
          path: 'timesheets',
          name: 'timesheets',
          component: () => import('./views/TimesheetsView.vue'),
        },
        {
          path: 'ai-activity',
          name: 'ai-activity',
          component: () => import('./views/AIActivityView.vue'),
          meta: { title: 'AI Activity' },
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          component: () => import('./views/SuppliersView.vue'),
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('./views/ContactsView.vue'),
        },
        {
          path: 'accounting',
          name: 'accounting',
          component: () => import('./views/AccountingView.vue'),
        },
        {
          path: 'projects',
          name: 'projects',
          component: () => import('./views/ProjectsView.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('./views/SettingsView.vue'),
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('./views/ReportsView.vue'),
          meta: { title: 'Reports' },
        },
        {
          // Embedded Report Writer designer (Report Writer engine). Admin-only
          // for the initial rollout (see the requiresTenantAdmin guard below).
          path: 'report-designer',
          name: 'report-designer',
          component: () => import('./views/ReportDesignerView.vue'),
          meta: { requiresTenantAdmin: true, title: 'Report Designer' },
        },
        {
          // Fill a Word/Excel template (authored outside Costed) with data.
          // Admin-only for the initial rollout, like the Report Designer.
          path: 'report-templates',
          name: 'report-templates',
          component: () => import('./views/ReportTemplatesView.vue'),
          meta: { requiresTenantAdmin: true },
        },
        {
          path: 'my-tasks',
          name: 'my-tasks',
          component: () => import('./views/MyTasksView.vue'),
          meta: { title: 'My Tasks' },
        },
        {
          path: 'documents',
          name: 'documents',
          component: () => import('./views/DocumentsView.vue'),
        },
        {
          path: 'transmittals',
          name: 'transmittals',
          component: () => import('./views/TransmittalsView.vue'),
        },
        {
          path: 'recycle-bin',
          name: 'recycle-bin',
          component: () => import('./views/RecycleBinView.vue'),
          meta: { title: 'Recycle Bin' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

// Dev-only: a login-free route to preview the embedded Report Writer designer
// locally without signing in. Never registered in production builds.
if (import.meta.env.DEV) {
  router.addRoute({
    path: '/report-designer-dev',
    name: 'report-designer-dev',
    component: () => import('./views/ReportDesignerView.vue'),
    meta: { requiresAuth: false, skipTenantContext: true },
  });
}

// Track if the portal session has been restored
let portalSessionRestoreAttempted = false;

// Navigation guard for auth
router.beforeEach(async (to, _from, next) => {
  // Client portal has its own separate auth subsystem — handle portal routes
  // entirely here and return, so the staff guard never runs for them.
  if (to.meta.portal) {
    const portalAuth = usePortalAuthStore();
    if (!portalSessionRestoreAttempted) {
      portalSessionRestoreAttempted = true;
      try {
        await portalAuth.restoreSession();
      } catch {
        /* ignore */
      }
    }
    const publicPortalPages = ['portal-login', 'portal-accept-invite', 'portal-forgot-password', 'portal-reset-password'];
    if (!publicPortalPages.includes(to.name as string) && !portalAuth.isAuthenticated) {
      next({ name: 'portal-login', params: { tenantSlug: to.params.tenantSlug } });
      return;
    }
    next();
    return;
  }

  const authStore = useAuthStore();

  // Hydrate the auth store from localStorage before making any auth decision.
  // restoreSession() is single-flight and short-circuits once hydrated, so
  // awaiting it on every navigation is effectively free after the first — and
  // it guarantees no navigation reads a half-restored store. (A second
  // navigation firing while the first restore was still in flight used to
  // observe isAuthenticated === false and bounce deep links to /login.)
  await authStore.restoreSession();

  // Check if route requires auth
  if (to.meta.requiresAuth !== false) {
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }
  }

  // Check for super admin access
  if (to.meta.requiresSuperAdmin) {
    if (authStore.user?.role !== 'super_admin') {
      next({ path: '/' });
      return;
    }
  }

  // Tenant-admin-only pages (e.g. the Report Designer rollout).
  if (to.meta.requiresTenantAdmin) {
    if (authStore.tenantRole !== 'admin' && authStore.tenantRole !== 'owner' && authStore.user?.role !== 'super_admin') {
      next({ path: '/' });
      return;
    }
  }

  // If going to login while authenticated, redirect to home
  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ path: '/' });
    return;
  }

  // Onboarding guard — only admin/owner users can access the setup wizard
  if (to.name === 'onboarding' && authStore.isAuthenticated && !authStore.isAdmin) {
    next({ path: '/dashboard' });
    return;
  }

  // Feature access guard — block hidden pages if config is loaded
  if (authStore.isAuthenticated && to.name && typeof to.name === 'string') {
    const pageKey = to.name as PageKey;
    if (pageKey in ALL_PAGES) {
      const { canAccessPage, loaded } = useFeatureAccess();
      if (loaded.value && !canAccessPage(pageKey)) {
        next({ path: '/dashboard' });
        return;
      }
    }
  }

  next();
});

// Create app
const app = createApp(App);

// Install plugins
app.use(createPinia());
app.use(router);

// Badge the browser tab for non-prod environments (🟠 STAGING / 🟢 DEV).
// Hostname-based, because staging + prod serve the same bundle.
setAppTitle();

// Mount app
installLegacyModalDrag();
app.mount('#app');

// Service worker (offline site conduct): production builds only, at the root scope.
//
// After a deploy the OLD worker still serves the cached shell for the first
// navigation, then the new build installs (skipWaiting + clientsClaim) and takes
// over — so that first visit runs stale code and a deep link such as
// /documents?doc=… silently does nothing. When the takeover happens within the
// first seconds of a page load, reload once (guarded so it can never loop);
// when it happens later, while someone may be mid-edit, just say so.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const loadedAt = Date.now();
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) { hadController = true; return; } // first install — nothing to announce
      const last = Number(sessionStorage.getItem('costed.sw.reloadedAt') || 0);
      if (Date.now() - loadedAt < 20_000 && Date.now() - last > 60_000) {
        sessionStorage.setItem('costed.sw.reloadedAt', String(Date.now()));
        window.location.reload();
        return;
      }
      import('@/composables/useToast').then(({ useToast }) =>
        useToast().info('Costed was updated — reload the page when convenient to get the latest version', { duration: 12000 } as never)).catch(() => { /* toast unavailable */ });
    });
    // Old builds registered a worker at /app/ that no longer exists; clear those only.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const r of regs) if (!r.scope.endsWith('/') || new URL(r.scope).pathname !== '/') r.unregister().catch(() => { /* best effort */ });
    }).catch(() => { /* best effort */ });
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => console.warn('[sw] registration failed', err));
  });
}
